/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Nucleus Admin API
 * Headless, multi-tenant commerce backend for jewelry brands.
 *
 * ## Surfaces
 *
 * | Prefix | Caller | Credential |
 * |---|---|---|
 * | `/platform/v1` | Platform operator | Platform token from **Platform: Auth** |
 * | `/admin/v1` | Staff of one organization | Staff token from **Admin: Auth** |
 * | `/storefront/v1` | A storefront | Store API key from **Admin: API keys** |
 * | `/public/v1` | Anyone (the brand application form) | None; rate limited per IP |
 *
 * Every credential is sent as `Authorization: Bearer <value>`. Use the **Authorize** button once and it applies to every request you try here.
 *
 * ## Conventions
 *
 * - **Ids** are UUID v7 strings. **Timestamps** are ISO 8601 in UTC. **Money** is `{ amount, currency }` with `amount` in integer minor units.
 * - **Lists** paginate by cursor: `?limit=20&cursor=...` in, `{ items, nextCursor }` out, `nextCursor` is `null` on the last page.
 * - **Errors** always use one envelope, `{ error: { code, message, requestId, details?, meta? } }`. Switch on `code`, never on `message`.
 * - **Request ids**: every response carries `x-request-id`. Quote it when reporting a problem.
 * - **Idempotency**: endpoints that create money-related state require an `Idempotency-Key` header; the header is listed on those endpoints.
 * - A record that belongs to another tenant answers **404**, never 403, so existence is not leaked.
 * OpenAPI spec version: 0.1.0
 */
import type {
  BlogCategory,
  BlogPost,
  ContentBlock,
  ContentSection,
  DeleteAdminV1OrgBlocksIdPathParameters,
  DeleteAdminV1StoresStoreIdBlocksIdPathParameters,
  DeleteAdminV1StoresStoreIdBlogCategoriesIdPathParameters,
  DeleteAdminV1StoresStoreIdBlogPostsIdPathParameters,
  DeleteAdminV1StoresStoreIdMenusIdPathParameters,
  DeleteAdminV1StoresStoreIdPagesIdPathParameters,
  DeleteAdminV1StoresStoreIdRedirectsIdPathParameters,
  DeleteAdminV1StoresStoreIdSectionsIdPathParameters,
  GetAdminV1OrgBlocks200,
  GetAdminV1OrgBlocksIdPathParameters,
  GetAdminV1OrgBlocksParams,
  GetAdminV1StoresStoreIdBlocks200,
  GetAdminV1StoresStoreIdBlocksIdPathParameters,
  GetAdminV1StoresStoreIdBlocksParams,
  GetAdminV1StoresStoreIdBlocksPathParameters,
  GetAdminV1StoresStoreIdBlogCategories200,
  GetAdminV1StoresStoreIdBlogCategoriesPathParameters,
  GetAdminV1StoresStoreIdBlogPosts200,
  GetAdminV1StoresStoreIdBlogPostsIdPathParameters,
  GetAdminV1StoresStoreIdBlogPostsParams,
  GetAdminV1StoresStoreIdBlogPostsPathParameters,
  GetAdminV1StoresStoreIdMenus200,
  GetAdminV1StoresStoreIdMenusIdPathParameters,
  GetAdminV1StoresStoreIdMenusPathParameters,
  GetAdminV1StoresStoreIdPages200,
  GetAdminV1StoresStoreIdPagesIdPathParameters,
  GetAdminV1StoresStoreIdPagesParams,
  GetAdminV1StoresStoreIdPagesPathParameters,
  GetAdminV1StoresStoreIdRedirects200,
  GetAdminV1StoresStoreIdRedirectsParams,
  GetAdminV1StoresStoreIdRedirectsPathParameters,
  GetAdminV1StoresStoreIdSections200,
  GetAdminV1StoresStoreIdSectionsIdPathParameters,
  GetAdminV1StoresStoreIdSectionsIdPreviewParams,
  GetAdminV1StoresStoreIdSectionsIdPreviewPathParameters,
  GetAdminV1StoresStoreIdSectionsParams,
  GetAdminV1StoresStoreIdSectionsPathParameters,
  Menu,
  Page,
  PatchAdminV1OrgBlocksIdBody,
  PatchAdminV1OrgBlocksIdPathParameters,
  PatchAdminV1StoresStoreIdBlocksIdBody,
  PatchAdminV1StoresStoreIdBlocksIdPathParameters,
  PatchAdminV1StoresStoreIdBlogCategoriesIdBody,
  PatchAdminV1StoresStoreIdBlogCategoriesIdPathParameters,
  PatchAdminV1StoresStoreIdBlogPostsIdBody,
  PatchAdminV1StoresStoreIdBlogPostsIdPathParameters,
  PatchAdminV1StoresStoreIdMenusIdBody,
  PatchAdminV1StoresStoreIdMenusIdPathParameters,
  PatchAdminV1StoresStoreIdPagesIdBody,
  PatchAdminV1StoresStoreIdPagesIdPathParameters,
  PatchAdminV1StoresStoreIdRedirectsIdBody,
  PatchAdminV1StoresStoreIdRedirectsIdPathParameters,
  PatchAdminV1StoresStoreIdSectionsIdBody,
  PatchAdminV1StoresStoreIdSectionsIdPathParameters,
  PostAdminV1OrgBlocksBody,
  PostAdminV1OrgBlocksPreviewBody,
  PostAdminV1OrgBlocksPreviewParams,
  PostAdminV1StoresStoreIdBlocksBody,
  PostAdminV1StoresStoreIdBlocksPathParameters,
  PostAdminV1StoresStoreIdBlocksPreviewBody,
  PostAdminV1StoresStoreIdBlocksPreviewParams,
  PostAdminV1StoresStoreIdBlocksPreviewPathParameters,
  PostAdminV1StoresStoreIdBlogCategoriesBody,
  PostAdminV1StoresStoreIdBlogCategoriesPathParameters,
  PostAdminV1StoresStoreIdBlogPostsBody,
  PostAdminV1StoresStoreIdBlogPostsPathParameters,
  PostAdminV1StoresStoreIdMenusBody,
  PostAdminV1StoresStoreIdMenusPathParameters,
  PostAdminV1StoresStoreIdPagesBody,
  PostAdminV1StoresStoreIdPagesPathParameters,
  PostAdminV1StoresStoreIdRedirectsBody,
  PostAdminV1StoresStoreIdRedirectsPathParameters,
  PostAdminV1StoresStoreIdSectionsBody,
  PostAdminV1StoresStoreIdSectionsPathParameters,
  PutAdminV1StoresStoreIdMenusIdItemsBody,
  PutAdminV1StoresStoreIdMenusIdItemsPathParameters,
  PutAdminV1StoresStoreIdSectionsIdBlocksBody,
  PutAdminV1StoresStoreIdSectionsIdBlocksPathParameters,
  Redirect,
  StorefrontContentBlock,
  StorefrontContentSection
} from '../model';

import { customInstance } from '../../../transport/mutator';
import type { BodyType } from '../../../transport/mutator';


type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];


  /**
 * @summary Blocks a store can place: its own and, with scope, the organization's shared ones
 */
export const getAdminV1StoresStoreIdBlocks = (
    { storeId }: GetAdminV1StoresStoreIdBlocksPathParameters,
    params?: GetAdminV1StoresStoreIdBlocksParams,
 options?: SecondParameter<typeof customInstance<GetAdminV1StoresStoreIdBlocks200>>,) => {
      return customInstance<GetAdminV1StoresStoreIdBlocks200>(
      {url: `/admin/v1/stores/${storeId}/blocks`, method: 'GET',
        params
    },
      options);
    }
  /**
 * A banner holds ordered items (one is a static banner, several a slider); a rail holds a collection or a hand-picked product list plus an optional header. Alt text is required on every image and video
 * @summary Create a banner or rail block in the store
 */
export const postAdminV1StoresStoreIdBlocks = (
    { storeId }: PostAdminV1StoresStoreIdBlocksPathParameters,
    postAdminV1StoresStoreIdBlocksBody: BodyType<PostAdminV1StoresStoreIdBlocksBody>,
 options?: SecondParameter<typeof customInstance<ContentBlock>>,) => {
      return customInstance<ContentBlock>(
      {url: `/admin/v1/stores/${storeId}/blocks`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: postAdminV1StoresStoreIdBlocksBody
    },
      options);
    }
  /**
 * Takes the block's content only (no key, name or schedule); alt text is optional, items may be empty and unknown references resolve to null instead of failing. Rail products are priced for the market and filtered by publication in this store. Nothing is written
 * @summary Resolve an unsaved block exactly as the storefront would receive it
 */
export const postAdminV1StoresStoreIdBlocksPreview = (
    { storeId }: PostAdminV1StoresStoreIdBlocksPreviewPathParameters,
    postAdminV1StoresStoreIdBlocksPreviewBody: BodyType<PostAdminV1StoresStoreIdBlocksPreviewBody>,
    params?: PostAdminV1StoresStoreIdBlocksPreviewParams,
 options?: SecondParameter<typeof customInstance<StorefrontContentBlock>>,) => {
      return customInstance<StorefrontContentBlock>(
      {url: `/admin/v1/stores/${storeId}/blocks/preview`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: postAdminV1StoresStoreIdBlocksPreviewBody,
        params
    },
      options);
    }
  /**
 * @summary Get a block; shared blocks are readable here too
 */
export const getAdminV1StoresStoreIdBlocksId = (
    { storeId, id }: GetAdminV1StoresStoreIdBlocksIdPathParameters,
 options?: SecondParameter<typeof customInstance<ContentBlock>>,) => {
      return customInstance<ContentBlock>(
      {url: `/admin/v1/stores/${storeId}/blocks/${id}`, method: 'GET'
    },
      options);
    }
  /**
 * @summary Update a store block; a shared block is 404 here and edited under /org/blocks
 */
export const patchAdminV1StoresStoreIdBlocksId = (
    { storeId, id }: PatchAdminV1StoresStoreIdBlocksIdPathParameters,
    patchAdminV1StoresStoreIdBlocksIdBody: BodyType<PatchAdminV1StoresStoreIdBlocksIdBody>,
 options?: SecondParameter<typeof customInstance<ContentBlock>>,) => {
      return customInstance<ContentBlock>(
      {url: `/admin/v1/stores/${storeId}/blocks/${id}`, method: 'PATCH',
      headers: {'Content-Type': 'application/json', },
      data: patchAdminV1StoresStoreIdBlocksIdBody
    },
      options);
    }
  /**
 * @summary Delete a store block; every section drops it
 */
export const deleteAdminV1StoresStoreIdBlocksId = (
    { storeId, id }: DeleteAdminV1StoresStoreIdBlocksIdPathParameters,
 options?: SecondParameter<typeof customInstance<void>>,) => {
      return customInstance<void>(
      {url: `/admin/v1/stores/${storeId}/blocks/${id}`, method: 'DELETE'
    },
      options);
    }
  /**
 * @summary Shared blocks of the organization
 */
export const getAdminV1OrgBlocks = (
    params?: GetAdminV1OrgBlocksParams,
 options?: SecondParameter<typeof customInstance<GetAdminV1OrgBlocks200>>,) => {
      return customInstance<GetAdminV1OrgBlocks200>(
      {url: `/admin/v1/org/blocks`, method: 'GET',
        params
    },
      options);
    }
  /**
 * A banner or a hand-picked rail; needs a membership that covers every store. Product CTAs and rail products resolve per store (each store shows what it has published); collection CTAs and collection-sourced rails are refused because a collection belongs to one store
 * @summary Create a shared block any store of the organization can place
 */
export const postAdminV1OrgBlocks = (
    postAdminV1OrgBlocksBody: BodyType<PostAdminV1OrgBlocksBody>,
 options?: SecondParameter<typeof customInstance<ContentBlock>>,) => {
      return customInstance<ContentBlock>(
      {url: `/admin/v1/org/blocks`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: postAdminV1OrgBlocksBody
    },
      options);
    }
  /**
 * Same as the store preview; storeId picks the store whose market, publication and collections resolve the content. Needs a membership that covers every store
 * @summary Resolve an unsaved shared block as one store of the organization would receive it
 */
export const postAdminV1OrgBlocksPreview = (
    postAdminV1OrgBlocksPreviewBody: BodyType<PostAdminV1OrgBlocksPreviewBody>,
    params: PostAdminV1OrgBlocksPreviewParams,
 options?: SecondParameter<typeof customInstance<StorefrontContentBlock>>,) => {
      return customInstance<StorefrontContentBlock>(
      {url: `/admin/v1/org/blocks/preview`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: postAdminV1OrgBlocksPreviewBody,
        params
    },
      options);
    }
  /**
 * @summary Get a shared block
 */
export const getAdminV1OrgBlocksId = (
    { id }: GetAdminV1OrgBlocksIdPathParameters,
 options?: SecondParameter<typeof customInstance<ContentBlock>>,) => {
      return customInstance<ContentBlock>(
      {url: `/admin/v1/org/blocks/${id}`, method: 'GET'
    },
      options);
    }
  /**
 * @summary Update a shared block
 */
export const patchAdminV1OrgBlocksId = (
    { id }: PatchAdminV1OrgBlocksIdPathParameters,
    patchAdminV1OrgBlocksIdBody: BodyType<PatchAdminV1OrgBlocksIdBody>,
 options?: SecondParameter<typeof customInstance<ContentBlock>>,) => {
      return customInstance<ContentBlock>(
      {url: `/admin/v1/org/blocks/${id}`, method: 'PATCH',
      headers: {'Content-Type': 'application/json', },
      data: patchAdminV1OrgBlocksIdBody
    },
      options);
    }
  /**
 * @summary Delete a shared block; sections in every store drop it
 */
export const deleteAdminV1OrgBlocksId = (
    { id }: DeleteAdminV1OrgBlocksIdPathParameters,
 options?: SecondParameter<typeof customInstance<void>>,) => {
      return customInstance<void>(
      {url: `/admin/v1/org/blocks/${id}`, method: 'DELETE'
    },
      options);
    }
  /**
 * @summary Sections of the store
 */
export const getAdminV1StoresStoreIdSections = (
    { storeId }: GetAdminV1StoresStoreIdSectionsPathParameters,
    params?: GetAdminV1StoresStoreIdSectionsParams,
 options?: SecondParameter<typeof customInstance<GetAdminV1StoresStoreIdSections200>>,) => {
      return customInstance<GetAdminV1StoresStoreIdSections200>(
      {url: `/admin/v1/stores/${storeId}/sections`, method: 'GET',
        params
    },
      options);
    }
  /**
 * A section is what a storefront reads by handle: GET /storefront/v1/sections/{handle}. Blocks may be the store's own or shared
 * @summary Create a section, optionally with its ordered blocks
 */
export const postAdminV1StoresStoreIdSections = (
    { storeId }: PostAdminV1StoresStoreIdSectionsPathParameters,
    postAdminV1StoresStoreIdSectionsBody: BodyType<PostAdminV1StoresStoreIdSectionsBody>,
 options?: SecondParameter<typeof customInstance<ContentSection>>,) => {
      return customInstance<ContentSection>(
      {url: `/admin/v1/stores/${storeId}/sections`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: postAdminV1StoresStoreIdSectionsBody
    },
      options);
    }
  /**
 * @summary Get a section
 */
export const getAdminV1StoresStoreIdSectionsId = (
    { storeId, id }: GetAdminV1StoresStoreIdSectionsIdPathParameters,
 options?: SecondParameter<typeof customInstance<ContentSection>>,) => {
      return customInstance<ContentSection>(
      {url: `/admin/v1/stores/${storeId}/sections/${id}`, method: 'GET'
    },
      options);
    }
  /**
 * @summary Update a section; a renamed handle leaves no redirect, sections are fragments
 */
export const patchAdminV1StoresStoreIdSectionsId = (
    { storeId, id }: PatchAdminV1StoresStoreIdSectionsIdPathParameters,
    patchAdminV1StoresStoreIdSectionsIdBody: BodyType<PatchAdminV1StoresStoreIdSectionsIdBody>,
 options?: SecondParameter<typeof customInstance<ContentSection>>,) => {
      return customInstance<ContentSection>(
      {url: `/admin/v1/stores/${storeId}/sections/${id}`, method: 'PATCH',
      headers: {'Content-Type': 'application/json', },
      data: patchAdminV1StoresStoreIdSectionsIdBody
    },
      options);
    }
  /**
 * @summary Delete a section; its blocks survive
 */
export const deleteAdminV1StoresStoreIdSectionsId = (
    { storeId, id }: DeleteAdminV1StoresStoreIdSectionsIdPathParameters,
 options?: SecondParameter<typeof customInstance<void>>,) => {
      return customInstance<void>(
      {url: `/admin/v1/stores/${storeId}/sections/${id}`, method: 'DELETE'
    },
      options);
    }
  /**
 * Position is the array index. Every id must be one of the store's blocks or a shared block
 * @summary Replace the ordered block list of a section
 */
export const putAdminV1StoresStoreIdSectionsIdBlocks = (
    { storeId, id }: PutAdminV1StoresStoreIdSectionsIdBlocksPathParameters,
    putAdminV1StoresStoreIdSectionsIdBlocksBody: BodyType<PutAdminV1StoresStoreIdSectionsIdBlocksBody>,
 options?: SecondParameter<typeof customInstance<ContentSection>>,) => {
      return customInstance<ContentSection>(
      {url: `/admin/v1/stores/${storeId}/sections/${id}/blocks`, method: 'PUT',
      headers: {'Content-Type': 'application/json', },
      data: putAdminV1StoresStoreIdSectionsIdBlocksBody
    },
      options);
    }
  /**
 * @summary The storefront payload of the section, drafts and scheduled-out blocks included
 */
export const getAdminV1StoresStoreIdSectionsIdPreview = (
    { storeId, id }: GetAdminV1StoresStoreIdSectionsIdPreviewPathParameters,
    params?: GetAdminV1StoresStoreIdSectionsIdPreviewParams,
 options?: SecondParameter<typeof customInstance<StorefrontContentSection>>,) => {
      return customInstance<StorefrontContentSection>(
      {url: `/admin/v1/stores/${storeId}/sections/${id}/preview`, method: 'GET',
        params
    },
      options);
    }
  /**
 * @summary Pages of the store, newest first
 */
export const getAdminV1StoresStoreIdPages = (
    { storeId }: GetAdminV1StoresStoreIdPagesPathParameters,
    params?: GetAdminV1StoresStoreIdPagesParams,
 options?: SecondParameter<typeof customInstance<GetAdminV1StoresStoreIdPages200>>,) => {
      return customInstance<GetAdminV1StoresStoreIdPages200>(
      {url: `/admin/v1/stores/${storeId}/pages`, method: 'GET',
        params
    },
      options);
    }
  /**
 * Templates give the page a shape: `faq` and `size_guide` read `blocks`, `policy` renders the store policies. `resolvedSeo` shows what the storefront will render and its warnings
 * @summary Create a page
 */
export const postAdminV1StoresStoreIdPages = (
    { storeId }: PostAdminV1StoresStoreIdPagesPathParameters,
    postAdminV1StoresStoreIdPagesBody: BodyType<PostAdminV1StoresStoreIdPagesBody>,
 options?: SecondParameter<typeof customInstance<Page>>,) => {
      return customInstance<Page>(
      {url: `/admin/v1/stores/${storeId}/pages`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: postAdminV1StoresStoreIdPagesBody
    },
      options);
    }
  /**
 * @summary One page
 */
export const getAdminV1StoresStoreIdPagesId = (
    { storeId, id }: GetAdminV1StoresStoreIdPagesIdPathParameters,
 options?: SecondParameter<typeof customInstance<Page>>,) => {
      return customInstance<Page>(
      {url: `/admin/v1/stores/${storeId}/pages/${id}`, method: 'GET'
    },
      options);
    }
  /**
 * @summary Update a page; renaming the handle writes a 301
 */
export const patchAdminV1StoresStoreIdPagesId = (
    { storeId, id }: PatchAdminV1StoresStoreIdPagesIdPathParameters,
    patchAdminV1StoresStoreIdPagesIdBody: BodyType<PatchAdminV1StoresStoreIdPagesIdBody>,
 options?: SecondParameter<typeof customInstance<Page>>,) => {
      return customInstance<Page>(
      {url: `/admin/v1/stores/${storeId}/pages/${id}`, method: 'PATCH',
      headers: {'Content-Type': 'application/json', },
      data: patchAdminV1StoresStoreIdPagesIdBody
    },
      options);
    }
  /**
 * @summary Delete a page
 */
export const deleteAdminV1StoresStoreIdPagesId = (
    { storeId, id }: DeleteAdminV1StoresStoreIdPagesIdPathParameters,
 options?: SecondParameter<typeof customInstance<void>>,) => {
      return customInstance<void>(
      {url: `/admin/v1/stores/${storeId}/pages/${id}`, method: 'DELETE'
    },
      options);
    }
  /**
 * @summary Redirects of the store with hit counts
 */
export const getAdminV1StoresStoreIdRedirects = (
    { storeId }: GetAdminV1StoresStoreIdRedirectsPathParameters,
    params?: GetAdminV1StoresStoreIdRedirectsParams,
 options?: SecondParameter<typeof customInstance<GetAdminV1StoresStoreIdRedirects200>>,) => {
      return customInstance<GetAdminV1StoresStoreIdRedirects200>(
      {url: `/admin/v1/stores/${storeId}/redirects`, method: 'GET',
        params
    },
      options);
    }
  /**
 * Chains are collapsed: anything that pointed at fromPath now points at toPath, and a redirect from toPath is removed
 * @summary Add a redirect
 */
export const postAdminV1StoresStoreIdRedirects = (
    { storeId }: PostAdminV1StoresStoreIdRedirectsPathParameters,
    postAdminV1StoresStoreIdRedirectsBody: BodyType<PostAdminV1StoresStoreIdRedirectsBody>,
 options?: SecondParameter<typeof customInstance<Redirect>>,) => {
      return customInstance<Redirect>(
      {url: `/admin/v1/stores/${storeId}/redirects`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: postAdminV1StoresStoreIdRedirectsBody
    },
      options);
    }
  /**
 * @summary Change where a redirect points
 */
export const patchAdminV1StoresStoreIdRedirectsId = (
    { storeId, id }: PatchAdminV1StoresStoreIdRedirectsIdPathParameters,
    patchAdminV1StoresStoreIdRedirectsIdBody: BodyType<PatchAdminV1StoresStoreIdRedirectsIdBody>,
 options?: SecondParameter<typeof customInstance<Redirect>>,) => {
      return customInstance<Redirect>(
      {url: `/admin/v1/stores/${storeId}/redirects/${id}`, method: 'PATCH',
      headers: {'Content-Type': 'application/json', },
      data: patchAdminV1StoresStoreIdRedirectsIdBody
    },
      options);
    }
  /**
 * @summary Remove a redirect
 */
export const deleteAdminV1StoresStoreIdRedirectsId = (
    { storeId, id }: DeleteAdminV1StoresStoreIdRedirectsIdPathParameters,
 options?: SecondParameter<typeof customInstance<void>>,) => {
      return customInstance<void>(
      {url: `/admin/v1/stores/${storeId}/redirects/${id}`, method: 'DELETE'
    },
      options);
    }
  /**
 * @summary Blog categories of the store, by position
 */
export const getAdminV1StoresStoreIdBlogCategories = (
    { storeId }: GetAdminV1StoresStoreIdBlogCategoriesPathParameters,
 options?: SecondParameter<typeof customInstance<GetAdminV1StoresStoreIdBlogCategories200>>,) => {
      return customInstance<GetAdminV1StoresStoreIdBlogCategories200>(
      {url: `/admin/v1/stores/${storeId}/blog/categories`, method: 'GET'
    },
      options);
    }
  /**
 * @summary Create a blog category
 */
export const postAdminV1StoresStoreIdBlogCategories = (
    { storeId }: PostAdminV1StoresStoreIdBlogCategoriesPathParameters,
    postAdminV1StoresStoreIdBlogCategoriesBody: BodyType<PostAdminV1StoresStoreIdBlogCategoriesBody>,
 options?: SecondParameter<typeof customInstance<BlogCategory>>,) => {
      return customInstance<BlogCategory>(
      {url: `/admin/v1/stores/${storeId}/blog/categories`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: postAdminV1StoresStoreIdBlogCategoriesBody
    },
      options);
    }
  /**
 * @summary Update a blog category; renaming writes a 301
 */
export const patchAdminV1StoresStoreIdBlogCategoriesId = (
    { storeId, id }: PatchAdminV1StoresStoreIdBlogCategoriesIdPathParameters,
    patchAdminV1StoresStoreIdBlogCategoriesIdBody: BodyType<PatchAdminV1StoresStoreIdBlogCategoriesIdBody>,
 options?: SecondParameter<typeof customInstance<BlogCategory>>,) => {
      return customInstance<BlogCategory>(
      {url: `/admin/v1/stores/${storeId}/blog/categories/${id}`, method: 'PATCH',
      headers: {'Content-Type': 'application/json', },
      data: patchAdminV1StoresStoreIdBlogCategoriesIdBody
    },
      options);
    }
  /**
 * @summary Delete a blog category; its posts stay and lose the category
 */
export const deleteAdminV1StoresStoreIdBlogCategoriesId = (
    { storeId, id }: DeleteAdminV1StoresStoreIdBlogCategoriesIdPathParameters,
 options?: SecondParameter<typeof customInstance<void>>,) => {
      return customInstance<void>(
      {url: `/admin/v1/stores/${storeId}/blog/categories/${id}`, method: 'DELETE'
    },
      options);
    }
  /**
 * @summary Blog posts of the store, newest created first (the storefront lists by publish date)
 */
export const getAdminV1StoresStoreIdBlogPosts = (
    { storeId }: GetAdminV1StoresStoreIdBlogPostsPathParameters,
    params?: GetAdminV1StoresStoreIdBlogPostsParams,
 options?: SecondParameter<typeof customInstance<GetAdminV1StoresStoreIdBlogPosts200>>,) => {
      return customInstance<GetAdminV1StoresStoreIdBlogPosts200>(
      {url: `/admin/v1/stores/${storeId}/blog/posts`, method: 'GET',
        params
    },
      options);
    }
  /**
 * `bodyHtml` is stored as written by staff and served verbatim; the storefront trusts its own staff. `publishedAt` in the future schedules the post: `live` stays false and the storefront answers 404 until then. `resolvedSeo` shows what the storefront will render
 * @summary Create a blog post
 */
export const postAdminV1StoresStoreIdBlogPosts = (
    { storeId }: PostAdminV1StoresStoreIdBlogPostsPathParameters,
    postAdminV1StoresStoreIdBlogPostsBody: BodyType<PostAdminV1StoresStoreIdBlogPostsBody>,
 options?: SecondParameter<typeof customInstance<BlogPost>>,) => {
      return customInstance<BlogPost>(
      {url: `/admin/v1/stores/${storeId}/blog/posts`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: postAdminV1StoresStoreIdBlogPostsBody
    },
      options);
    }
  /**
 * @summary One blog post
 */
export const getAdminV1StoresStoreIdBlogPostsId = (
    { storeId, id }: GetAdminV1StoresStoreIdBlogPostsIdPathParameters,
 options?: SecondParameter<typeof customInstance<BlogPost>>,) => {
      return customInstance<BlogPost>(
      {url: `/admin/v1/stores/${storeId}/blog/posts/${id}`, method: 'GET'
    },
      options);
    }
  /**
 * @summary Update a blog post; renaming the handle writes a 301
 */
export const patchAdminV1StoresStoreIdBlogPostsId = (
    { storeId, id }: PatchAdminV1StoresStoreIdBlogPostsIdPathParameters,
    patchAdminV1StoresStoreIdBlogPostsIdBody: BodyType<PatchAdminV1StoresStoreIdBlogPostsIdBody>,
 options?: SecondParameter<typeof customInstance<BlogPost>>,) => {
      return customInstance<BlogPost>(
      {url: `/admin/v1/stores/${storeId}/blog/posts/${id}`, method: 'PATCH',
      headers: {'Content-Type': 'application/json', },
      data: patchAdminV1StoresStoreIdBlogPostsIdBody
    },
      options);
    }
  /**
 * @summary Delete a blog post
 */
export const deleteAdminV1StoresStoreIdBlogPostsId = (
    { storeId, id }: DeleteAdminV1StoresStoreIdBlogPostsIdPathParameters,
 options?: SecondParameter<typeof customInstance<void>>,) => {
      return customInstance<void>(
      {url: `/admin/v1/stores/${storeId}/blog/posts/${id}`, method: 'DELETE'
    },
      options);
    }
  /**
 * Items whose target was deleted or unpublished carry `targetMissing: true`; the storefront drops them
 * @summary Menus of the store with their item trees
 */
export const getAdminV1StoresStoreIdMenus = (
    { storeId }: GetAdminV1StoresStoreIdMenusPathParameters,
 options?: SecondParameter<typeof customInstance<GetAdminV1StoresStoreIdMenus200>>,) => {
      return customInstance<GetAdminV1StoresStoreIdMenus200>(
      {url: `/admin/v1/stores/${storeId}/menus`, method: 'GET'
    },
      options);
    }
  /**
 * Items link to a url or to a store product, collection, page, blog post or blog category by id; three levels deep, 200 items at most
 * @summary Create a menu with its items
 */
export const postAdminV1StoresStoreIdMenus = (
    { storeId }: PostAdminV1StoresStoreIdMenusPathParameters,
    postAdminV1StoresStoreIdMenusBody: BodyType<PostAdminV1StoresStoreIdMenusBody>,
 options?: SecondParameter<typeof customInstance<Menu>>,) => {
      return customInstance<Menu>(
      {url: `/admin/v1/stores/${storeId}/menus`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: postAdminV1StoresStoreIdMenusBody
    },
      options);
    }
  /**
 * @summary One menu
 */
export const getAdminV1StoresStoreIdMenusId = (
    { storeId, id }: GetAdminV1StoresStoreIdMenusIdPathParameters,
 options?: SecondParameter<typeof customInstance<Menu>>,) => {
      return customInstance<Menu>(
      {url: `/admin/v1/stores/${storeId}/menus/${id}`, method: 'GET'
    },
      options);
    }
  /**
 * @summary Rename a menu
 */
export const patchAdminV1StoresStoreIdMenusId = (
    { storeId, id }: PatchAdminV1StoresStoreIdMenusIdPathParameters,
    patchAdminV1StoresStoreIdMenusIdBody: BodyType<PatchAdminV1StoresStoreIdMenusIdBody>,
 options?: SecondParameter<typeof customInstance<Menu>>,) => {
      return customInstance<Menu>(
      {url: `/admin/v1/stores/${storeId}/menus/${id}`, method: 'PATCH',
      headers: {'Content-Type': 'application/json', },
      data: patchAdminV1StoresStoreIdMenusIdBody
    },
      options);
    }
  /**
 * @summary Delete a menu
 */
export const deleteAdminV1StoresStoreIdMenusId = (
    { storeId, id }: DeleteAdminV1StoresStoreIdMenusIdPathParameters,
 options?: SecondParameter<typeof customInstance<void>>,) => {
      return customInstance<void>(
      {url: `/admin/v1/stores/${storeId}/menus/${id}`, method: 'DELETE'
    },
      options);
    }
  /**
 * @summary Replace the whole item tree of a menu
 */
export const putAdminV1StoresStoreIdMenusIdItems = (
    { storeId, id }: PutAdminV1StoresStoreIdMenusIdItemsPathParameters,
    putAdminV1StoresStoreIdMenusIdItemsBody: BodyType<PutAdminV1StoresStoreIdMenusIdItemsBody>,
 options?: SecondParameter<typeof customInstance<Menu>>,) => {
      return customInstance<Menu>(
      {url: `/admin/v1/stores/${storeId}/menus/${id}/items`, method: 'PUT',
      headers: {'Content-Type': 'application/json', },
      data: putAdminV1StoresStoreIdMenusIdItemsBody
    },
      options);
    }
  export type GetAdminV1StoresStoreIdBlocksResult = NonNullable<Awaited<ReturnType<typeof getAdminV1StoresStoreIdBlocks>>>
export type PostAdminV1StoresStoreIdBlocksResult = NonNullable<Awaited<ReturnType<typeof postAdminV1StoresStoreIdBlocks>>>
export type PostAdminV1StoresStoreIdBlocksPreviewResult = NonNullable<Awaited<ReturnType<typeof postAdminV1StoresStoreIdBlocksPreview>>>
export type GetAdminV1StoresStoreIdBlocksIdResult = NonNullable<Awaited<ReturnType<typeof getAdminV1StoresStoreIdBlocksId>>>
export type PatchAdminV1StoresStoreIdBlocksIdResult = NonNullable<Awaited<ReturnType<typeof patchAdminV1StoresStoreIdBlocksId>>>
export type DeleteAdminV1StoresStoreIdBlocksIdResult = NonNullable<Awaited<ReturnType<typeof deleteAdminV1StoresStoreIdBlocksId>>>
export type GetAdminV1OrgBlocksResult = NonNullable<Awaited<ReturnType<typeof getAdminV1OrgBlocks>>>
export type PostAdminV1OrgBlocksResult = NonNullable<Awaited<ReturnType<typeof postAdminV1OrgBlocks>>>
export type PostAdminV1OrgBlocksPreviewResult = NonNullable<Awaited<ReturnType<typeof postAdminV1OrgBlocksPreview>>>
export type GetAdminV1OrgBlocksIdResult = NonNullable<Awaited<ReturnType<typeof getAdminV1OrgBlocksId>>>
export type PatchAdminV1OrgBlocksIdResult = NonNullable<Awaited<ReturnType<typeof patchAdminV1OrgBlocksId>>>
export type DeleteAdminV1OrgBlocksIdResult = NonNullable<Awaited<ReturnType<typeof deleteAdminV1OrgBlocksId>>>
export type GetAdminV1StoresStoreIdSectionsResult = NonNullable<Awaited<ReturnType<typeof getAdminV1StoresStoreIdSections>>>
export type PostAdminV1StoresStoreIdSectionsResult = NonNullable<Awaited<ReturnType<typeof postAdminV1StoresStoreIdSections>>>
export type GetAdminV1StoresStoreIdSectionsIdResult = NonNullable<Awaited<ReturnType<typeof getAdminV1StoresStoreIdSectionsId>>>
export type PatchAdminV1StoresStoreIdSectionsIdResult = NonNullable<Awaited<ReturnType<typeof patchAdminV1StoresStoreIdSectionsId>>>
export type DeleteAdminV1StoresStoreIdSectionsIdResult = NonNullable<Awaited<ReturnType<typeof deleteAdminV1StoresStoreIdSectionsId>>>
export type PutAdminV1StoresStoreIdSectionsIdBlocksResult = NonNullable<Awaited<ReturnType<typeof putAdminV1StoresStoreIdSectionsIdBlocks>>>
export type GetAdminV1StoresStoreIdSectionsIdPreviewResult = NonNullable<Awaited<ReturnType<typeof getAdminV1StoresStoreIdSectionsIdPreview>>>
export type GetAdminV1StoresStoreIdPagesResult = NonNullable<Awaited<ReturnType<typeof getAdminV1StoresStoreIdPages>>>
export type PostAdminV1StoresStoreIdPagesResult = NonNullable<Awaited<ReturnType<typeof postAdminV1StoresStoreIdPages>>>
export type GetAdminV1StoresStoreIdPagesIdResult = NonNullable<Awaited<ReturnType<typeof getAdminV1StoresStoreIdPagesId>>>
export type PatchAdminV1StoresStoreIdPagesIdResult = NonNullable<Awaited<ReturnType<typeof patchAdminV1StoresStoreIdPagesId>>>
export type DeleteAdminV1StoresStoreIdPagesIdResult = NonNullable<Awaited<ReturnType<typeof deleteAdminV1StoresStoreIdPagesId>>>
export type GetAdminV1StoresStoreIdRedirectsResult = NonNullable<Awaited<ReturnType<typeof getAdminV1StoresStoreIdRedirects>>>
export type PostAdminV1StoresStoreIdRedirectsResult = NonNullable<Awaited<ReturnType<typeof postAdminV1StoresStoreIdRedirects>>>
export type PatchAdminV1StoresStoreIdRedirectsIdResult = NonNullable<Awaited<ReturnType<typeof patchAdminV1StoresStoreIdRedirectsId>>>
export type DeleteAdminV1StoresStoreIdRedirectsIdResult = NonNullable<Awaited<ReturnType<typeof deleteAdminV1StoresStoreIdRedirectsId>>>
export type GetAdminV1StoresStoreIdBlogCategoriesResult = NonNullable<Awaited<ReturnType<typeof getAdminV1StoresStoreIdBlogCategories>>>
export type PostAdminV1StoresStoreIdBlogCategoriesResult = NonNullable<Awaited<ReturnType<typeof postAdminV1StoresStoreIdBlogCategories>>>
export type PatchAdminV1StoresStoreIdBlogCategoriesIdResult = NonNullable<Awaited<ReturnType<typeof patchAdminV1StoresStoreIdBlogCategoriesId>>>
export type DeleteAdminV1StoresStoreIdBlogCategoriesIdResult = NonNullable<Awaited<ReturnType<typeof deleteAdminV1StoresStoreIdBlogCategoriesId>>>
export type GetAdminV1StoresStoreIdBlogPostsResult = NonNullable<Awaited<ReturnType<typeof getAdminV1StoresStoreIdBlogPosts>>>
export type PostAdminV1StoresStoreIdBlogPostsResult = NonNullable<Awaited<ReturnType<typeof postAdminV1StoresStoreIdBlogPosts>>>
export type GetAdminV1StoresStoreIdBlogPostsIdResult = NonNullable<Awaited<ReturnType<typeof getAdminV1StoresStoreIdBlogPostsId>>>
export type PatchAdminV1StoresStoreIdBlogPostsIdResult = NonNullable<Awaited<ReturnType<typeof patchAdminV1StoresStoreIdBlogPostsId>>>
export type DeleteAdminV1StoresStoreIdBlogPostsIdResult = NonNullable<Awaited<ReturnType<typeof deleteAdminV1StoresStoreIdBlogPostsId>>>
export type GetAdminV1StoresStoreIdMenusResult = NonNullable<Awaited<ReturnType<typeof getAdminV1StoresStoreIdMenus>>>
export type PostAdminV1StoresStoreIdMenusResult = NonNullable<Awaited<ReturnType<typeof postAdminV1StoresStoreIdMenus>>>
export type GetAdminV1StoresStoreIdMenusIdResult = NonNullable<Awaited<ReturnType<typeof getAdminV1StoresStoreIdMenusId>>>
export type PatchAdminV1StoresStoreIdMenusIdResult = NonNullable<Awaited<ReturnType<typeof patchAdminV1StoresStoreIdMenusId>>>
export type DeleteAdminV1StoresStoreIdMenusIdResult = NonNullable<Awaited<ReturnType<typeof deleteAdminV1StoresStoreIdMenusId>>>
export type PutAdminV1StoresStoreIdMenusIdItemsResult = NonNullable<Awaited<ReturnType<typeof putAdminV1StoresStoreIdMenusIdItems>>>
